import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11 } from "styled-components";
import React from "react";
import { Wrap } from "~/features/ui/wrap";
import { Grid } from "~/features/ui/grid";
import { Headline } from "~/features/ui/headline";
import { takeFirst } from "~/features/app/utils";
import { Link } from "~/features/ui/link";
import { Button } from "~/features/ui/button";
import { type Mode } from "~/features/ui/get-theme-style";
import { isLinkValid } from "~/features/ui/link/utils";
import ArrowRight from "~/public/icons/arrow-right.svg";
import ArrowTopRight from "~/public/icons/arrow-top-right.svg";
type PlainLink = Record<string, unknown> & {
  href?: string;
  newWindow?: boolean;
  text?: React.ReactNode;
};
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
function getPlainLinkIcon(link?: PlainLink | null) {
  if (!link) {
    return undefined;
  }
  return link.newWindow ? ArrowTopRight : ArrowRight;
}
export type TextMediaBlock1_2ViewProps = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  hasSpacer?: boolean | null;
  hasTopDivider?: boolean | null;
  headline?: string | null;
  link?: PlainLink | Array<PlainLink | null | undefined> | null;
  mediaDesktop?: React.ReactNode;
  mediaMobile?: React.ReactNode;
  mediaPosition?: string | null;
  mode?: string | null;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  text?: React.ReactNode;
  theme?: string | null;
};
export function TextMediaBlock1_2View({
  anchor,
  hasSpacer = false,
  hasTopDivider = true,
  headline,
  link,
  mediaDesktop,
  mediaMobile,
  mediaPosition = "right",
  mode = "light",
  sectionProps,
  text,
  theme = "white"
}: TextMediaBlock1_2ViewProps) {
  const cta = takeFirst(link);
  const icon = getPlainLinkIcon(cta);
  const textCol = <_StyledDiv key="text-col" $_css={[{
    "gridColumn": "1 / -1",
    "display": "flex",
    "height": "100%",
    "flexDirection": "column",
    "@media (min-width: 1024px)": {
      "gridColumn": "span 6 / span 6"
    }
  }]}>
      {(hasSpacer || headline) && <_StyledDiv2 $_css2={[{
      "borderBottomWidth": "1px",
      "padding": "var(--spacing-xl)",
      "@media (min-width: 1024px)": {
        "padding": "var(--spacing-2xl)"
      }
    }, !headline && {
      "flex": "1 1 0%"
    }, hasSpacer && {
      "display": "none",
      "@media (min-width: 1024px)": {
        "display": "block"
      }
    }]}>
          {headline && <_StyledDiv3>
              <_StyledHeadline type="h2" text={headline} />
            </_StyledDiv3>}
        </_StyledDiv2>}
      <_StyledDiv4 $_css3={[{
      "display": "block",
      "borderBottomWidth": "1px",
      "padding": "var(--spacing-md)",
      "@media (min-width: 1024px)": {
        "display": "none"
      }
    }]}>{mediaMobile}</_StyledDiv4>
      <_StyledDiv5 $_css4={[{
      "position": "relative",
      "zIndex": "10",
      "display": "flex",
      "flexDirection": "column",
      "justifyContent": "center",
      "gap": "var(--spacing-lg)",
      "padding": "var(--spacing-xl)",
      "@media (min-width: 1024px)": {
        "padding": "var(--spacing-2xl)"
      }
    }, (headline || !hasSpacer) && {
      "flex": "1 1 0%"
    }]}>
        {text}
        {isLinkValid(cta) && <div>
            <Button as={Link} {...cta} icon={icon} variant="underline" />
          </div>}
      </_StyledDiv5>
    </_StyledDiv>;
  const mediaCol = <_StyledDiv6 key="media-col" $_css5={[{
    "gridColumn": "span 6 / span 6",
    "display": "none",
    "padding": "var(--spacing-xl)",
    "@media (min-width: 1024px)": {
      "display": "block"
    }
  }]}>
      <_StyledDiv7>{mediaDesktop}</_StyledDiv7>
    </_StyledDiv6>;
  const columns = mediaPosition === "right" ? [textCol, mediaCol] : [mediaCol, textCol];
  return <_StyledSection {...sectionProps} data-theme={mode as Mode} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css6={[{
    "position": "relative",
    "color": "var(--text-primary)"
  },
  // v3 uses true white for the "white" theme; the component previously
  // hardcoded grey and ignored the theme field entirely.
  theme === "grey" ? {
    "backgroundColor": "var(--bg-secondary)"
  } : {
    "backgroundColor": "var(--bg-primary)"
  }, hasTopDivider && {
    "borderTopWidth": "1px",
    "borderColor": "var(--stroke-primary)"
  }]}>
      <_StyledWrap>
        <_StyledGrid $_css7={[{
        "paddingLeft": "var(--spacing-xl)",
        "paddingRight": "var(--spacing-xl)",
        "@media (min-width: 1024px)": {
          "minHeight": "650px"
        }
      }]}>{columns}</_StyledGrid>
      </_StyledWrap>
    </_StyledSection>;
}
var _StyledDiv = /*#__PURE__*/_styled("div").withConfig({
  componentId: "sc-sf-1orrpx-0"
})`${p => p.$_css}`;
var _StyledDiv2 = _styled2("div").withConfig({
  componentId: "sc-sf-1orrpx-1"
})`${p => p.$_css2}`;
var _StyledDiv3 = _styled3("div").withConfig({
  componentId: "sc-sf-1orrpx-2"
})({
  "position": "relative",
  "zIndex": "10"
});
var _StyledHeadline = _styled4(Headline).withConfig({
  componentId: "sc-sf-1orrpx-3"
})({
  "fontSize": "2.4rem",
  "lineHeight": "1.2",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "3.8rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.038rem",
    "fontWeight": "400"
  }
});
var _StyledDiv4 = _styled5("div").withConfig({
  componentId: "sc-sf-1orrpx-4"
})`${p => p.$_css3}`;
var _StyledDiv5 = _styled6("div").withConfig({
  componentId: "sc-sf-1orrpx-5"
})`${p => p.$_css4}`;
var _StyledDiv6 = _styled7("div").withConfig({
  componentId: "sc-sf-1orrpx-6"
})`${p => p.$_css5}`;
var _StyledDiv7 = _styled8("div").withConfig({
  componentId: "sc-sf-1orrpx-7"
})({
  "position": "relative",
  "height": "100%",
  "width": "100%"
});
var _StyledSection = _styled9("section").withConfig({
  componentId: "sc-sf-1orrpx-8"
})`${p => p.$_css6}`;
var _StyledWrap = _styled10(Wrap).withConfig({
  componentId: "sc-sf-1orrpx-9"
})({
  "borderWidth": "0px"
});
var _StyledGrid = /*#__PURE__*/_styled11(Grid).withConfig({
  displayName: "text-media1_2___StyledGrid",
  componentId: "sc-7wqhs0-0"
})(["", ""], p => p.$_css7);